import React, { useState, useEffect, useRef } from "react";
import Layout from "./Layout";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaPaperPlane,
  FaPaperclip,
  FaSpinner,
  FaTimes,
  FaFileAlt,
  FaChartLine,
  FaStop,
  FaBullseye,
  FaCalculator,
  FaEye,
} from "react-icons/fa";
import "./MiniShotDetail.css";

// Import the reports
import RealityCheck from "./RealityCheck";
import ClarityCheck from "./ClarityCheck";
import FogIndex from "./FogIndex";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// --- Custom Gemini Sparkle Icon Component ---
const GeminiIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="gemini-icon-svg"
  >
    <path
      d="M10.5 3.5C10.5 2.67157 11.1716 2 12 2C12.8284 2 13.5 2.67157 13.5 3.5V8.5C13.5 9.32843 14.1716 10 15 10H20.5C21.3284 10 22 10.6716 22 11.5C22 12.3284 21.3284 13 20.5 13H15C14.1716 13 13.5 13.6716 13.5 14.5V19.5C13.5 20.3284 12.8284 21 12 21C11.1716 21 10.5 20.3284 10.5 19.5V14.5C10.5 13.6716 9.82843 13 9 13H3.5C2.67157 13 2 12.3284 2 11.5C2 10.6716 2.67157 10 3.5 10H9C9.82843 10 10.5 9.32843 10.5 8.5V3.5Z"
      fill="url(#gemini-gradient)"
    />
    <defs>
      <linearGradient
        id="gemini-gradient"
        x1="2"
        y1="2"
        x2="22"
        y2="21"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#4facfe" />
        <stop offset="1" stopColor="#f093fb" />
      </linearGradient>
    </defs>
  </svg>
);

// --- File Manager Component ---
const FileManager = ({ files, onDelete, onClose }) => (
  <div className="file-manager-overlay">
    <div className="file-manager-modal">
      <div className="file-manager-header">
        <h3>📂 Project Files</h3>
        <button onClick={onClose} className="close-btn"><FaTimes /></button>
      </div>
      <div className="file-manager-list">
        {files.length === 0 ? (
          <p className="no-files">No files uploaded yet.</p>
        ) : (
          files.map((file, index) => (
            <div key={`${ file.fileId }-${ index }`} className="file-item">
              <div className="file-info">
                <span className="file-icon">
                  {file.fileType.includes("image") ? "🖼️" :
                    file.fileType.includes("pdf") ? "📕" :
                      file.fileType.includes("sheet") || file.fileType.includes("csv") ? "📊" : "📄"}
                </span>
                <div className="file-details">
                  <span className="file-name" title={file.fileName}>{file.fileName}</span>
                  <span className="file-date">{new Date(file.timestamp).toLocaleDateString()}</span>
                </div>
              </div>
              <button
                className="delete-file-btn"
                onClick={() => onDelete(file.fileId, file.messageId)}
                title="Delete File"
              >
                🗑️
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  </div>
);

export default function MiniShotDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  // --- State ---
  const [miniShot, setMiniShot] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isChatHistoryLoaded, setIsChatHistoryLoaded] = useState(false);

  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState(""); // Restored inputMessage
  const [isLoading, setIsLoading] = useState(false);    // Restored isLoading
  const [isStreaming, setIsStreaming] = useState(false); // Restored isStreaming
  const [selectedFile, setSelectedFile] = useState(null);

  const [uploadedFileId, setUploadedFileId] = useState(null);

  // -- NEW: Upload Simulation State --
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const uploadIntervalRef = useRef(null);
  const [hasGeneratedInitialMessage, setHasGeneratedInitialMessage] =
    useState(false);
  const [error, setError] = useState(null);

  // --- File Manager State ---
  const [showFileManager, setShowFileManager] = useState(false);

  // Derive file list from chat history
  const attachedFiles = messages
    .filter((msg) => msg.fileAttached)
    .map((msg) => ({
      ...msg.fileAttached,
      messageId: msg.id,
      timestamp: msg.timestamp,
    }));

  const handleDeleteFile = async (fileId, messageId) => {
    if (!window.confirm("Are you sure you want to delete this file? This cannot be undone.")) return;

    // 1. Optimistic Update (Immediate UI Change)
    // Store previous state in case we need to revert
    const previousMessages = [...messages];

    // Update UI immediately (Optimistic Update)
    setMessages((prevMessages) =>
      prevMessages.map((msg) =>
        String(msg.id) === String(messageId)
          ? { ...msg, fileAttached: null, content: msg.content + " (File Deleted)" }
          : msg
      )
    );

    if (uploadedFileId === fileId) {
      removeFile();
    }

    try {
      // 2. Call Backend API (Background)
      const response = await fetch(`${ API_BASE_URL }/api/chat/messages/${ messageId }/attachment`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        let errorMsg = "Failed to delete file from server";
        try {
          const errorData = await response.json();
          if (errorData.error) errorMsg = errorData.error;
        } catch (e) { /* ignore JSON parse error */ }
        throw new Error(errorMsg);
      }

      // Success: Do nothing (UI is already updated)

    } catch (error) {
      console.error("Delete file error:", error);
      alert(`Failed to delete file: ${ error.message }`);

      // 3. Revert UI on Failure
      setMessages(previousMessages);
      // Optional: Add the file back to selection if it was removed? 
      // (Complexity warning: simplistic revert might be enough)
    }
  };

  // --- Refs ---
  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);
  const textareaRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const isUserNearBottom = useRef(true);

  const isStreamingRef = useRef(false);

  // --- Canvas Mode State ---
  const [showReportPanel, setShowReportPanel] = useState(false);
  const [activeReportTab, setActiveReportTab] = useState("clarity");

  useEffect(() => {
    isStreamingRef.current = isStreaming;
  }, [isStreaming]);

  // --- 1. Fetch MiniShot Details ---
  useEffect(() => {
    const fetchMiniShotDetails = async () => {
      try {
        // We set loading, but we DON'T block the render of the component structure
        setIsPageLoading(true);
        const response = await fetch(`${ API_BASE_URL }/api/minishots/${ id }`, {
          credentials: "include",
        });
        if (!response.ok) throw new Error("Failed to load MiniShot details");
        const data = await response.json();
        setMiniShot(data);
      } catch (err) {
        console.error(err);
        setError("Could not load project details.");
      } finally {
        setIsPageLoading(false);
      }
    };
    if (id) fetchMiniShotDetails();
  }, [id]);

  // --- 2. Scroll Logic ---
  useEffect(() => {
    const el = chatMessagesRef.current;
    if (!el) return;
    const handleScroll = () => {
      const threshold = 120;
      const atBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
      isUserNearBottom.current = atBottom;
      setShowScrollButton(!atBottom);
    };
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isStreaming && isUserNearBottom.current) {
      scrollToBottom();
    }
  }, [messages, isStreaming, showReportPanel]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // --- 3. Load Chat History ---
  useEffect(() => {
    if (id) loadChatHistory();
  }, [id]);

  // --- 4. Adjust Textarea ---
  useEffect(() => {
    adjustTextareaHeight();
  }, [inputMessage]);

  const isSystemPrompt = (content) => {
    if (!content || typeof content !== "string") return false;
    const lower = content.toLowerCase().trim();

    // Check for various system prompt signatures
    return (
      lower.startsWith("as an ai startup analyst") ||
      lower.startsWith("you are an ai") ||
      lower.includes("system prompt:") ||
      lower.includes("**startup title:**") ||
      // ✅ NEW: Specifically catch the Moonshot Context message
      lower.includes("you are moonshot ai") ||
      lower.includes("founder-provided information:") ||
      lower.includes("startup context")
    );
  };

  const processMessages = (rawMessages) => {
    if (!Array.isArray(rawMessages)) return [];

    const filtered = rawMessages.filter((msg) => {
      if (!msg) return false;
      const role = (msg.role || "").toLowerCase();
      if (role === "system") return false;
      if (role === "user" && msg.content && isSystemPrompt(msg.content))
        return false;
      return role === "user" || role === "assistant";
    });

    const formatted = filtered.map((msg) => ({
      id:
        msg._id ||
        msg.id ||
        `msg-${ Date.now() }-${ Math.random().toString(36).substr(2, 9) }`,
      role: (msg.role || "").toLowerCase(),
      content: msg.content || "",
      timestamp: msg.createdAt || new Date().toISOString(),
      fileAttached: msg.fileAttached,
      isStreaming: msg.isStreaming || false,
    }));

    const uniqueMap = new Map();
    formatted.forEach((msg) => uniqueMap.set(msg.id, msg));

    return Array.from(uniqueMap.values()).sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      const diff = timeA - timeB;
      if (diff !== 0) return diff;
      if (a.role === "user" && b.role === "assistant") return -1;
      if (a.role === "assistant" && b.role === "user") return 1;
      return String(a.id).localeCompare(String(b.id));
    });
  };

  const loadChatHistory = async () => {
    try {
      const response = await fetch(
        `${ API_BASE_URL }/api/minishots/${ id }/messages`,
        {
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) throw new Error(`HTTP ${ response.status }`);
      const data = await response.json();

      const rawMessages = Array.isArray(data?.messages) ? data.messages : [];
      const processed = processMessages(rawMessages);

      if (isStreamingRef.current) {
        setMessages((prev) => processMessages([...prev, ...processed]));
      } else {
        setMessages(processed);
      }

      const hasWelcomeMessage = processed.some(
        (msg) =>
          msg.role === "assistant" &&
          msg.content &&
          msg.content.toLowerCase().includes("## welcome & analysis")
      );
      if (hasWelcomeMessage) setHasGeneratedInitialMessage(true);
    } catch (error) {
      console.error("Chat load error:", error);
      setError("Failed to load chat history");
    } finally {
      setIsChatHistoryLoaded(true);
    }
  };

  // --- 5. Generate Initial Message Logic ---
  useEffect(() => {
    if (
      miniShot &&
      isChatHistoryLoaded &&
      messages.length === 0 &&
      !hasGeneratedInitialMessage &&
      !isPageLoading &&
      !isStreamingRef.current
    ) {
      setHasGeneratedInitialMessage(true);
      generateInitialAIMessage();
    }
  }, [
    miniShot,
    isChatHistoryLoaded,
    messages.length,
    hasGeneratedInitialMessage,
    isPageLoading,
  ]);

  const generateInitialAIMessage = async () => {
    if (messages.length > 0) return;

    setIsStreaming(true);
    isStreamingRef.current = true;
    setError(null);

    const aiMessageId = Date.now();
    const aiMessage = {
      id: aiMessageId,
      role: "assistant",
      content: "",
      timestamp: new Date().toISOString(),
      isStreaming: true,
    };

    setMessages((prev) => [...prev, aiMessage]);

    try {
      abortControllerRef.current = new AbortController();
      const initialPrompt = createPersonalizedPrompt(miniShot);
      const requestBody = {
        miniShotId: String(id),
        message: initialPrompt,
        isInitialGeneration: true,
      };

      const response = await fetch(
        `${ API_BASE_URL }/api/minishots/${ id }/analysis-chat/stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
          },
          credentials: "include",
          body: JSON.stringify(requestBody),
          signal: abortControllerRef.current.signal,
        }
      );

      if (!response.ok) throw new Error(`HTTP ${ response.status }`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === "content") {
                // DIRECT UPDATE + THROTTLING (ChatGPT Speed)
                // Split content into small pieces and delay slightly
                const content = data.content || "";
                const chars = content.split("");

                for (const char of chars) {
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === aiMessageId
                        ? { ...msg, content: msg.content + char }
                        : msg
                    )
                  );
                  // 15ms delay per character = ~4000 chars/min (Readable but flowy)
                  await new Promise(r => setTimeout(r, 15));
                }

              } else if (data.type === "complete") {
                // Done
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === aiMessageId
                      ? { ...msg, isStreaming: false, id: data.messageId }
                      : msg
                  )
                );
                setIsStreaming(false);
                isStreamingRef.current = false;

              } else if (data.type === "error") {
                throw new Error(data.message);
              }
            } catch (e) { }
          }
        }
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === aiMessageId
              ? {
                ...msg,
                content: createFallbackAnalysis(miniShot),
                isStreaming: false,
                error: false,
              }
              : msg
          )
        );
      }
      setIsStreaming(false);
      isStreamingRef.current = false;
    } finally {
      abortControllerRef.current = null;
    }
  };

  //   const createPersonalizedPrompt = (ms) => {
  //     const { title, initialQuestions = [] } = ms || {};
  //     let prompt = `As an AI startup analyst, your job is to evaluate the user’s moonshot startup idea with strict neutrality, skepticism, and realism. Do not assume the idea is good. Only rely on the user’s actual answers.

  // Your goal is NOT to praise, motivate, or encourage. Your goal is ONLY to assess viability, risks, and flaws.

  // Avoid positive bias. If something is unclear, weak, unrealistic, or unproven, call it out directly.:\n\n*Startup Title:* ${
  //       title || "Untitled Project"
  //     }\n\n*User's Responses:*\n`;

  //     if (Array.isArray(initialQuestions)) {
  //       initialQuestions.forEach((q) => {
  //         if (q.question && q.answer) {
  //           prompt += `• **${q.question}:** ${q.answer}\n`;
  //         }
  //       });
  //     }
  //     prompt += `\nProvide a structured response:

  // ## 1. Summary
  // Summarize the idea strictly based on the user’s answers. If information is missing, state that it is missing.

  // ## 2. Strengths
  // Identify strengths *only if* they appear clearly in the user’s answers.  
  // If none are evident, say so.

  // ## 3. Opportunities
  // Describe any potential opportunities that logically follow from the information provided.

  // ## 4. Weaknesses / Gaps
  // Identify weaknesses, limitations, unclear areas, or missing information based only on the user's inputs.

  // ## 5. Market Considerations
  // Discuss market factors relevant to the idea using the details given.  
  // If not enough info exists, state that.

  // ## 6. Next Steps
  // Suggest reasonable next steps based on the stage and information provided.

  // ## 7. Strategic Questions
  // Ask 2–3 questions that would help clarify the idea or fill gaps in understanding.`;
  //     return prompt;
  //   };



  const createPersonalizedPrompt = (ms) => {
    const { title, initialQuestions = [] } = ms || {};

    let prompt = `
You are Moonshot AI — a neutral analytical assistant focused on clarity and decision-making.


This is the **Main Discussion Chat**, not a final verdict or pitch review.
Your role is to help the founder **think clearly**, identify blind spots, and improve decision-making
based strictly on logic, evidence, and market realities.

If ideas are broad or ambitious, guide them toward a smaller, testable first version instead of rejecting them.


### Core Principles
- Be professional, serious, and analytical.
- Do NOT analyze jokes, hypotheticals, roleplay, or harmful content.
- Do NOT motivate, flatter, or encourage emotionally.
- Do NOT assume success, intent, or competence.
- Base all reasoning on **physics, economics, incentives, and market behavior**.
- If something is unclear or unsupported, explicitly say so.

### How This Chat Differs from Reports
- Reports deliver conclusions.
- This chat explores assumptions, trade-offs, risks, and improvement paths.
- You may reference prior reports if relevant, but do not repeat them verbatim.

---

## Startup Context
**Project Name:** ${ title || "Untitled Project" }

**Founder-Provided Information:**
`;

    if (Array.isArray(initialQuestions)) {
      initialQuestions.forEach((q) => {
        if (q.question && q.answer) {
          prompt += `- ${ q.question }: ${ q.answer }\n`;
        }
      });
    }

    prompt += `

---

## Response Guidelines

Structure your responses clearly and concisely.

### 1. Situation Analysis
Restate what is currently known about the startup.
If critical data is missing, explicitly flag it.

### 2. Key Assumptions
Identify assumptions the startup appears to be making
(e.g., user behavior, pricing power, adoption speed, regulation, cost structure).

### 3. Risks & Constraints
Highlight technical, economic, operational, or market risks.
Be precise. Avoid vague statements.

### 4. Improvement Levers
Suggest **specific, realistic** ways the startup could improve viability
(e.g., scope reduction, market narrowing, pricing change, tech simplification).

### 5. Trade-Offs
Explain what the founder gains and loses with different strategic choices.

### 6. Focused Follow-Up Questions
Ask 2–3 high-signal questions that would materially change the analysis.

---

## Final Rule
If an idea is weak, say why.
If something is promising, explain under what conditions it could work.
Truth > optimism.
`;

    return prompt;
  };

  const createFallbackAnalysis = (ms) =>
    `## Welcome! 🚀\nI'm excited to explore **${ ms?.title }** with you.\n\n## Initial Thoughts\nGreat start! Let's refine your strategy.\n\nWhat would you like to discuss first?`;

  // const handleFileSelect = async (event) => {
  //   const file = event.target.files[0];
  //   if (!file) return;
  //   setError(null);

  //   if (file.size > 10 * 1024 * 1024) {
  //     setError("File size too large. Please select a file smaller than 10MB.");
  //     return;
  //   }

  //   const allowedTypes = [
  //     "application/pdf",
  //     "application/msword",
  //     "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  //     "text/plain",
  //     "text/csv",
  //     "application/vnd.ms-excel",
  //     "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  //     "image/jpeg",
  //     "image/jpg",
  //     "image/png",
  //     "image/gif",
  //   ];

  //   if (!allowedTypes.includes(file.type)) {
  //     setError("Unsupported file type.");
  //     return;
  //   }

  //   setSelectedFile(file);
  //   const formData = new FormData();
  //   formData.append("file", file);

  //   try {
  //     const response = await fetch(`${API_BASE_URL}/api/files/upload`, {
  //       method: "POST",
  //       credentials: "include",
  //       body: formData,
  //     });
  //     if (response.ok) {
  //       const data = await response.json();
  //       setUploadedFileId(data.file.id);
  //     } else {
  //       setSelectedFile(null);
  //       setError("File upload failed.");
  //     }
  //   } catch (error) {
  //     setSelectedFile(null);
  //     setError("File upload error.");
  //   }
  // };
  // const handleFileSelect = async (event) => {
  //     const file = event.target.files[0];
  //     if (!file) return;
  //     setError(null);

  //     // --- REMOVED SIZE LIMIT CHECK HERE --- 

  //     const allowedTypes = [
  //       "application/pdf",
  //       "application/msword",
  //       "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  //       "text/plain",
  //       "text/csv",
  //       "application/vnd.ms-excel",
  //       "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  //       "image/jpeg",
  //       "image/jpg",
  //       "image/png",
  //       "image/gif",
  //     ];

  //     if (!allowedTypes.includes(file.type)) {
  //       setError("Unsupported file type.");
  //       return;
  //     }

  //     setSelectedFile(file);
  //     const formData = new FormData();
  //     formData.append("file", file);

  //     // Start Simulation
  //     setIsUploading(true);
  //     setUploadProgress(0);
  //     setError(null); // Clear previous errors

  //     // Clear any existing interval
  //     if (uploadIntervalRef.current) clearInterval(uploadIntervalRef.current);

  //     let progress = 0;
  //     uploadIntervalRef.current = setInterval(() => {
  //       progress += Math.random() * 15; // Random increment
  //       if (progress >= 100) {
  //         progress = 100;
  //         clearInterval(uploadIntervalRef.current);
  //         // "Finish" upload
  //         setIsUploading(false);
  //         setUploadProgress(100);
  //         setSelectedFile(file);
  //       }
  //       setUploadProgress(progress);
  //     }, 200);
  //   };



  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setError(null);
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
    ];
    if (!allowedTypes.includes(file.type)) {
      setError("Unsupported file type.");
      return;
    }
    setSelectedFile(file);
    const formData = new FormData();
    formData.append("file", file);
    // ✅ REAL UPLOAD (Restored)
    setIsUploading(true);
    setUploadProgress(10); // Start progress
    try {
      const response = await fetch(`${ API_BASE_URL }/api/files/upload`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        setUploadedFileId(data.file.id); // This ID is crucial for the backend!
        setUploadProgress(100);
        setIsUploading(false);
      } else {
        setSelectedFile(null);
        setError("File upload failed.");
        setIsUploading(false);
      }
    } catch (error) {
      console.error(error);
      setSelectedFile(null);
      setError("File upload error.");
      setIsUploading(false);
    }
  };




  const cancelUpload = (e) => { // Added 'e' parameter for resetting input
    if (uploadIntervalRef.current) clearInterval(uploadIntervalRef.current);
    setIsUploading(false);
    setUploadProgress(0);
    setSelectedFile(null);
    if (e && e.target) e.target.value = null; // Reset input

    // Show feedback
    const tempId = Date.now();
    setMessages(prev => [...prev, {
      id: tempId,
      role: 'system', // or internal
      content: 'File upload stopped.',
      isError: true
    }]);

    // Clear feedback after 2s
    setTimeout(() => {
      setMessages(prev => prev.filter(m => m.id !== tempId));
    }, 2000);
  };

  const removeFile = () => {
    setSelectedFile(null);
    setUploadedFileId(null);
    setUploadProgress(0);
    const fileInput = document.getElementById("file-upload");
    if (fileInput) fileInput.value = "";
  };
  const streamBufferRef = useRef("");
  const pendingFinalizationRef = useRef(null);
  const typingIntervalRef = useRef(null);

  // CONSUMER: Typing Effect Logic
  useEffect(() => {
    if (isStreaming) {
      typingIntervalRef.current = setInterval(() => {
        if (streamBufferRef.current.length > 0) {
          // Take a small chunk (1-3 chars) to mimic typing
          const chunkSize = Math.floor(Math.random() * 3) + 1;
          const chunk = streamBufferRef.current.slice(0, chunkSize);
          streamBufferRef.current = streamBufferRef.current.slice(chunkSize);

          setMessages((prev) => {
            // Find duplicate check to prevent potential double-renders
            const lastMsg = prev[prev.length - 1];
            if (lastMsg && lastMsg.isStreaming) {
              return prev.map((msg, idx) =>
                idx === prev.length - 1 ? { ...msg, content: msg.content + chunk } : msg
              );
            }
            return prev;
          });
        } else if (pendingFinalizationRef.current) {
          // Buffer empty + Finalization pending = DONE
          const data = pendingFinalizationRef.current;

          setMessages((prev) =>
            prev.map((msg) => {
              // Update AI Message ID
              if (msg.isStreaming) { // Identify by streaming flag
                return { ...msg, isStreaming: false, id: data.messageId };
              }
              // Update User Message ID
              if (String(msg.id) === String(data.tempUserMessageId) && data.userMessageId) {
                return { ...msg, id: data.userMessageId };
              }
              return msg;
            })
          );

          // Cleanup
          setIsStreaming(false);
          isStreamingRef.current = false;
          pendingFinalizationRef.current = null;
          clearInterval(typingIntervalRef.current);
        }
      }, 20); // 20ms = ~3000 chars/min (very fast reading speed but smooth)
    }

    return () => {
      if (typingIntervalRef.current) clearInterval(typingIntervalRef.current);
    };
  }, [isStreaming]);


  const handleSendMessage = async () => {
    if (!inputMessage.trim() && !uploadedFileId) return;
    if (isLoading || isStreaming) return;
    setError(null);

    const userMessage = {
      id: Date.now(),
      role: "user",
      content: inputMessage || "📎 File uploaded",
      timestamp: new Date().toISOString(),
      fileAttached: selectedFile
        ? { fileName: selectedFile.name, fileType: selectedFile.type }
        : null,
    };

    setMessages((prev) => [...prev, userMessage]);

    const messageToSend =
      inputMessage.trim() ||
      (selectedFile
        ? `I've uploaded a file: ${ selectedFile.name }. Please analyze it.`
        : "");
    setInputMessage("");

    // Reset Buffers
    streamBufferRef.current = "";
    pendingFinalizationRef.current = null;

    const aiMessageId = Date.now() + 1;
    setMessages((prev) => [
      ...prev,
      {
        id: aiMessageId,
        role: "assistant",
        content: "", // Start empty
        timestamp: new Date(new Date().getTime() + 1).toISOString(),
        isStreaming: true,
      },
    ]);

    setIsStreaming(true);
    isStreamingRef.current = true;

    try {
      abortControllerRef.current = new AbortController();

      const requestBody = {
        miniShotId: String(id),
        message: messageToSend,
      };

      if (uploadedFileId && selectedFile) {
        requestBody.fileId = String(uploadedFileId);
        requestBody.fileName = String(selectedFile.name);
        requestBody.fileMimeType = String(selectedFile.type);
      }

      const response = await fetch(
        `${ API_BASE_URL }/api/minishots/${ id }/analysis-chat/stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
          },
          credentials: "include",
          body: JSON.stringify(requestBody),
          signal: abortControllerRef.current.signal,
        }
      );

      if (!response.ok) throw new Error(`HTTP ${ response.status }`);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      // PRODUCER LOOP
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === "content") {
                // Buffer the content
                streamBufferRef.current += data.content;
              } else if (data.type === "complete") {
                // Queue finalization (include user message temp ID)
                pendingFinalizationRef.current = {
                  ...data,
                  tempUserMessageId: userMessage.id
                };
              } else if (data.type === "error") throw new Error(data.message);

            } catch (e) { }
          }
        }
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === aiMessageId
              ? {
                ...msg,
                content: "Error generating response.",
                isStreaming: false,
                error: true,
              }
              : msg
          )
        );
      }
      setIsStreaming(false);
      isStreamingRef.current = false;
    } finally {
      removeFile();
      abortControllerRef.current = null;
    }
  };

  const stopStreaming = () => {
    abortControllerRef.current?.abort();
    setIsStreaming(false);
    isStreamingRef.current = false;
  };
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        Math.min(textareaRef.current.scrollHeight, 120) + "px";
    }
  };

  const formatMessageContent = (content) => {
    if (!content) return content;

    let cleanContent = content.replace(
      /\[Attached File Content\]:[\s\S]*$/,
      ""
    );

    cleanContent = cleanContent
      .replace(/\(VTL[^)]*\)/g, "")
      .replace(/\|[-=|]*\|/g, "")
      .replace(/[-]{3,}/g, "")
      .replace(/[|]{2,}/g, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();

    const codeBlocks = [];
    cleanContent = cleanContent.replace(/```([\s\S]*?)```/g, (match, code) => {
      codeBlocks.push(code);
      return `__CODE_BLOCK_${ codeBlocks.length - 1 }__`;
    });

    const lines = cleanContent.split("\n");
    let inTable = false;
    let tableHtml = [];
    let formattedLines = [];

    lines.forEach((line) => {
      if (line.trim().startsWith("|")) {
        if (!inTable) {
          inTable = true;
          tableHtml.push('<table class="ai-table"><tbody>');
        }
        const rowContent = line.trim().replace(/^\||\|$/g, "");
        const cells = rowContent.split("|").map((c) => c.trim());
        const isSeparator = cells.some((c) => c.match(/^[-:]+$/));
        if (!isSeparator) {
          let rowHtml = "<tr>";
          cells.forEach((cell) => {
            let cellText = cell
              .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
              .replace(/`([^`]+)`/g, "<code class='inline-code'>$1</code>");
            if (cellText.match(/^[0-9️⃣]/))
              cellText = `<strong>${ cellText }</strong>`;
            rowHtml += `<td>${ cellText }</td>`;
          });
          rowHtml += "</tr>";
          tableHtml.push(rowHtml);
        }
      } else {
        if (inTable) {
          inTable = false;
          tableHtml.push("</tbody></table>");
          formattedLines.push(tableHtml.join(""));
          tableHtml = [];
        }
        formattedLines.push(line);
      }
    });
    if (inTable) {
      tableHtml.push("</tbody></table>");
      formattedLines.push(tableHtml.join(""));
    }

    let formatted = formattedLines.join("\n");

    formatted = formatted
      .replace(/^###\s+(.+)$/gm, '<h3 class="ai-header">$1</h3>')
      .replace(/^##\s+(.+)$/gm, '<h2 class="ai-header">$1</h2>')
      .replace(/^#\s+(.+)$/gm, '<h1 class="ai-header">$1</h1>')
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      .replace(/(?<!\*)\*([^*\n]+?)\*(?!\*)/g, "<em>$1</em>")
      .replace(/`([^`]+)`/g, "<code class='inline-code'>$1</code>")
      .replace(/^[•·-]\s*(.+)$/gm, '<li class="bullet-item">$1</li>')
      .replace(/^\d+\.\s*(.+)$/gm, '<li class="numbered-item">$1</li>')
      .replace(/\n/g, "<br/>");

    formatted = formatted.replace(
      /__CODE_BLOCK_(\d+)__/g,
      (match, index) =>
        `<pre class="code-block"><code>${ codeBlocks[index] }</code></pre>`
    );

    formatted = formatted
      .replace(
        /(<li class="bullet-item">.*?<\/li>(?:\s*<br\/>)*)+/gs,
        (match) => `<ul class="ai-list">${ match.replace(/<br\/>/g, "") }</ul>`
      )
      .replace(
        /(<li class="numbered-item">.*?<\/li>(?:\s*<br\/>)*)+/gs,
        (match) =>
          `<ol class="ai-list">${ match
            .replace(/<br\/>/g, "")
            .replace(/class="numbered-item"/g, "") }</ol>`
      )
      .replace(/<br\/>\s*<h/g, "<h")
      .replace(/<\/h([1-6])>\s*<br\/>/g, "</h$1>")
      .replace(/<br\/>\s*<table/g, "<table")
      .replace(/<\/table>\s*<br\/>/g, "</table>");

    return (
      <div
        className="formatted-message"
        dangerouslySetInnerHTML={{ __html: formatted }}
      />
    );
  };

  // --- ERROR HANDLING (Keep layout intact) ---
  if (error && !miniShot)
    return (
      <Layout fullWidth={true}>
        <div className="error-state">
          <h2>Not Found</h2>
          <button onClick={() => navigate("/mainPage")}>Back</button>
        </div>
      </Layout>
    );

  // --- MAIN RENDER (Header stays visible even during loading) ---
  return (
    <Layout fullWidth={true} shouldCollapse={showReportPanel}>
      <div
        className={`minishot-detail-wrapper ${ showReportPanel ? "panel-open" : ""
          }`}
      >
        {/* LEFT: Chat Section */}
        <div className="chat-section">
          {/* Header - ALWAYS RENDERED */}
          <div className="chat-header">
            <div className="header-left">
              <button
                onClick={() => navigate("/mainPage")}
                className="back-button"
                title="Back to Dashboard"
                style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: "1.2rem", display: "flex", alignItems: "center" }}
              >
                <FaArrowLeft />
              </button>

              <div className="title-container">
                {/* If minishot loaded, show title. If loading, show placeholder */}
                <h1>{miniShot ? miniShot.title : "Creating Moonshot..."}</h1>
                <div className="model-badge">
                  <GeminiIcon />
                  <span>Moonshot Map</span>
                </div>
              </div>
            </div>
            <div className="header-right">
              <button
                className="view-files-btn"
                onClick={() => setShowFileManager(true)}
                title="View & Manage Files"
                style={{ marginRight: '8px' }}
              >
                <span className="btn-icon">📂</span> <span className="mobile-hide-text">Files ({attachedFiles.length})</span>
              </button>
              <button
                onClick={() => setShowReportPanel(!showReportPanel)}
                className={`view-reports-btn ${ showReportPanel ? "active" : ""
                  }`}
                // Disable button until data loads
                disabled={isPageLoading}
                style={{ opacity: isPageLoading ? 0.5 : 1 }}
              >
                {showReportPanel ? <FaTimes /> : <FaChartLine />}
                <span className="mobile-hide-text" style={{ marginLeft: "6px" }}>
                  {showReportPanel ? "Close" : "Reports"}
                </span>
              </button>
            </div>
          </div>

          {/* Messages Area - Show Spinner INSIDE if loading */}
          {isPageLoading ? (
            <div className="loading-state-overlay">
              <FaSpinner
                className="spin"
                style={{ fontSize: "2rem", color: "#0b57d0" }}
              />
              <p style={{ marginTop: "1rem", color: "#666" }}>
                Initializing chat...
              </p>
            </div>
          ) : (
            <div className="chat-messages" ref={chatMessagesRef}>
              {messages.map((message) => (
                <div key={message.id} className={`message ${ message.role }`}>
                  {message.role === "assistant" && (
                    <div className="ai-avatar-container">
                      <div className="ai-avatar-icon">
                        <GeminiIcon />
                      </div>
                    </div>
                  )}
                  <div className="message-content-wrapper">
                    {message.fileAttached && (
                      <div className="file-attachment">
                        <FaFileAlt />{" "}
                        <span>{message.fileAttached.fileName}</span>
                      </div>
                    )}
                    <div className="message-text">
                      {formatMessageContent(message.content)}
                    </div>
                    {message.isStreaming && (
                      <div className="typing-indicator">
                        <span>●</span>
                        <span>●</span>
                        <span>●</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Scroll Button Overlay */}
          {showScrollButton && (
            <button className="scroll-to-bottom-btn" onClick={scrollToBottom}>
              ↓ New Messages
            </button>
          )}

          {/* Input Area */}
          <div className="chat-input-wrapper">
            <div className="chat-input-container">
              {/* File Upload Preview Area (Gemini Style) */}
              {(selectedFile || isUploading) && (
                <div className="file-preview-area">
                  <div className={`file-card ${ isUploading ? "uploading" : "" }`}>
                    {/* Icon based on state */}
                    <div className="file-icon-wrapper">
                      {isUploading ? (
                        <div className="spinner-xs"></div>
                      ) : (
                        <div className="file-type-icon">PDF</div>
                        // Note: Simple placeholder, can be dynamic based on type later
                      )}
                    </div>

                    <div className="file-info">
                      <span className="file-name">
                        {isUploading ? "Uploading..." : selectedFile?.name}
                      </span>
                      {isUploading && (
                        <div className="upload-progress-track">
                          <div
                            className="upload-progress-fill"
                            style={{ width: `${ uploadProgress }%` }}
                          />
                        </div>
                      )}
                    </div>

                    <button
                      className="remove-file-btn"
                      onClick={isUploading ? cancelUpload : removeFile}
                      title={isUploading ? "Cancel Upload" : "Remove File"}
                    >
                      <FaTimes />
                    </button>
                  </div>
                </div>
              )}

              <div className="input-controls-row">
                <label htmlFor="file-upload" className="icon-btn file-btn">
                  <FaPaperclip />
                </label>
                <input
                  type="file"
                  id="file-upload"
                  onChange={handleFileSelect}
                  hidden
                  accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                />

                <textarea
                  ref={textareaRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Message Moonshot AI..."
                  rows={1}
                  disabled={isPageLoading} // Disable until loaded
                />

                {isStreaming ? (
                  <button onClick={stopStreaming} className="icon-btn stop-btn">
                    <FaStop />
                  </button>
                ) : (
                  <button
                    onClick={handleSendMessage}
                    className="icon-btn send-btn"
                    disabled={
                      (!inputMessage.trim() && !uploadedFileId) ||
                      isLoading ||
                      isPageLoading
                    }
                  >
                    <FaPaperPlane />
                  </button>
                )}
              </div>
            </div>
            <div className="input-footer-text">
              Copyright - MoonShotMap - 2025
            </div>
          </div>
        </div>

        {/* RIGHT: Reports Panel */}
        {showReportPanel && (
          <div className="report-canvas">
            <div className="canvas-tabs">
              <button
                className={activeReportTab === "clarity" ? "active" : ""}
                onClick={() => setActiveReportTab("clarity")}
              >
                <FaEye /> Clarity
              </button>
              <button
                className={activeReportTab === "reality" ? "active" : ""}
                onClick={() => setActiveReportTab("reality")}
              >
                <FaBullseye /> Reality
              </button>
              <button
                className={activeReportTab === "fog" ? "active" : ""}
                onClick={() => setActiveReportTab("fog")}
              >
                <FaCalculator /> Fog Index
              </button>

              {/* --- NEW: Mobile Close Button --- */}
              <button
                className="mobile-report-close"
                onClick={() => setShowReportPanel(false)}
                aria-label="Close Report"
              >
                <FaTimes />
              </button>
            </div>
            <div className="canvas-content-scroller">
              {activeReportTab === "clarity" && (
                <ClarityCheck embedded={true} miniShotId={id} />
              )}
              {activeReportTab === "reality" && (
                <RealityCheck embedded={true} miniShotId={id} />
              )}
              {activeReportTab === "fog" && (
                <FogIndex embedded={true} miniShotId={id} />
              )}
            </div>
          </div>
        )}
      </div>
      {/* --- FILE MANAGER OVERLAY --- */}
      {showFileManager && (
        <FileManager
          files={attachedFiles}
          onDelete={handleDeleteFile}
          onClose={() => setShowFileManager(false)}
        />
      )}
    </Layout>
  );
}
